import React, { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button/Button';
import { Input } from '@/components/ui/Input';
import { Alert } from '@/components/ui/Alert/Alert';
import { Badge } from '@/components/ui/Badge/Badge';
import { LocationAutocomplete, type LocationData } from '@/components/ui/LocationAutocomplete/LocationAutocomplete';
import {
  AlertCircle,
  MapPin,
  X,
  Plus,
  Loader2,
  Layers,
  Sparkles,
  ArrowRightToLine,
  ArrowLeftFromLine,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ResearchService, type ResearchDestination, type DestinationWithCount } from '@/services/ResearchService';

interface EditRegionModalProps {
  isOpen: boolean;
  onClose: () => void;
  region: DestinationWithCount;
  eventId: string;
  onUpdate: (updatedRegion: DestinationWithCount) => void;
  /** Standalone city destinations from the same event (not in any region) */
  standaloneDestinations?: DestinationWithCount[];
}

export function EditRegionModal({
  isOpen,
  onClose,
  region,
  eventId,
  onUpdate,
  standaloneDestinations = [],
}: EditRegionModalProps) {
  const [displayName, setDisplayName] = useState(region.display_name || '');
  const [children, setChildren] = useState<ResearchDestination[]>([]);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [removingCityId, setRemovingCityId] = useState<string | null>(null);
  const [showAddCity, setShowAddCity] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [manualCityName, setManualCityName] = useState('');
  const [addingCity, setAddingCity] = useState(false);
  const [isRediscovering, setIsRediscovering] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [movingInId, setMovingInId] = useState<string | null>(null);
  const [extractingId, setExtractingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const researchService = new ResearchService();

  // Load children when modal opens
  useEffect(() => {
    if (isOpen && region.id) {
      loadChildren();
    }
  }, [isOpen, region.id]);

  // Reset state when modal opens with different region
  useEffect(() => {
    if (isOpen) {
      setDisplayName(region.display_name || '');
      setError(null);
      setShowAddCity(false);
      setSearchValue('');
      setManualCityName('');
    }
  }, [isOpen, region.id]);

  async function loadChildren() {
    try {
      setLoadingChildren(true);
      const result = await researchService.getRegionChildren(region.id);
      setChildren(result);
    } catch (err: any) {
      setError('Failed to load region cities');
    } finally {
      setLoadingChildren(false);
    }
  }

  async function handleRemoveCity(cityId: string) {
    try {
      setRemovingCityId(cityId);
      await researchService.removeCityFromRegion(cityId);
      setChildren(prev => prev.filter(c => c.id !== cityId));
    } catch (err: any) {
      setError(err.message || 'Failed to remove city');
    } finally {
      setRemovingCityId(null);
    }
  }

  async function handleAddCityFromSearch(location: LocationData) {
    const cityName = location.city || location.formatted_address.split(',')[0].trim();
    const countryName = location.country || region.country;

    // Check for duplicates
    const exists = children.some(
      c => c.city.toLowerCase() === cityName.toLowerCase() && c.country.toLowerCase() === countryName.toLowerCase()
    );
    if (exists) {
      setError(`${cityName} is already in this region`);
      setSearchValue('');
      return;
    }

    try {
      setAddingCity(true);
      setError(null);
      const newChild = await researchService.addCityToRegion(region.id, {
        city: cityName,
        country: countryName,
        place_id: location.place_id,
        location_json: {
          formatted_address: location.formatted_address,
          place_id: location.place_id,
          geometry: {
            location: {
              lat: location.lat,
              lng: location.lon,
            },
          },
        },
      });
      setChildren(prev => [...prev, newChild]);
      setSearchValue('');
    } catch (err: any) {
      setError(err.message || 'Failed to add city');
    } finally {
      setAddingCity(false);
    }
  }

  async function handleAddManualCity() {
    if (!manualCityName.trim()) return;

    const exists = children.some(
      c => c.city.toLowerCase() === manualCityName.trim().toLowerCase()
    );
    if (exists) {
      setError(`${manualCityName.trim()} is already in this region`);
      setManualCityName('');
      return;
    }

    try {
      setAddingCity(true);
      setError(null);
      const newChild = await researchService.addCityToRegion(region.id, {
        city: manualCityName.trim(),
        country: region.country,
      });
      setChildren(prev => [...prev, newChild]);
      setManualCityName('');
    } catch (err: any) {
      setError(err.message || 'Failed to add city');
    } finally {
      setAddingCity(false);
    }
  }

  async function handleRediscoverCities() {
    try {
      setIsRediscovering(true);
      setError(null);
      const discovered = await researchService.discoverCities(
        displayName || region.display_name || region.city,
        region.country
      );
      // Add any newly discovered cities that aren't already in the list
      const existingNames = new Set(children.map(c => c.city.toLowerCase()));
      const newCities = discovered.filter(d => !existingNames.has(d.city.toLowerCase()));

      for (const city of newCities) {
        try {
          const newChild = await researchService.addCityToRegion(region.id, {
            city: city.city,
            country: city.country,
          });
          setChildren(prev => [...prev, newChild]);
        } catch {
          // Skip duplicates or failures silently
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to re-discover cities');
    } finally {
      setIsRediscovering(false);
    }
  }

  async function handleMoveIn(destId: string) {
    try {
      setMovingInId(destId);
      setError(null);
      await researchService.moveDestinationToRegion(destId, region.id);
      // Reload children to reflect the move
      await loadChildren();
      // Trigger parent refresh
      onUpdate({ ...region, display_name: displayName.trim() || region.display_name });
    } catch (err: any) {
      setError(err.message || 'Failed to move destination into region');
    } finally {
      setMovingInId(null);
    }
  }

  async function handleExtract(cityId: string) {
    try {
      setExtractingId(cityId);
      setError(null);
      await researchService.moveDestinationToRegion(cityId, null);
      setChildren(prev => prev.filter(c => c.id !== cityId));
      // Trigger parent refresh
      onUpdate({ ...region, display_name: displayName.trim() || region.display_name });
    } catch (err: any) {
      setError(err.message || 'Failed to extract city from region');
    } finally {
      setExtractingId(null);
    }
  }

  async function handleSave() {
    const trimmedName = displayName.trim();
    if (!trimmedName) {
      setError('Region name is required');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      // Only update if name changed
      if (trimmedName !== (region.display_name || '')) {
        await researchService.updateDestination(region.id, {
          display_name: trimmedName,
        });
      }

      // Notify parent with updated region data
      onUpdate({
        ...region,
        display_name: trimmedName,
        children: children.map(c => ({
          ...c,
          shortlist_count: 0,
          venue_count: 0,
        })),
      });

      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  }

  function handleClose() {
    setError(null);
    setShowAddCity(false);
    setSearchValue('');
    setManualCityName('');
    onClose();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      title="Edit Region"
      description={`Manage cities in ${region.display_name || region.city}`}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!displayName.trim() || children.length === 0 || isSaving}
            isLoading={isSaving}
          >
            Save Changes
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Region Name */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Region Name</label>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g., Tuscany, Croatian Islands"
            disabled={isSaving}
          />
        </div>

        {/* Region Info */}
        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <Layers className="w-5 h-5 text-primary-600 flex-shrink-0" />
          <div className="flex-1">
            <span className="text-sm text-gray-700">
              {region.country}
            </span>
            {region.region_source && (
              <Badge variant="secondary" className="ml-2 text-xs">
                {region.region_source === 'google_places' ? 'Google Places' : region.region_source}
              </Badge>
            )}
          </div>
        </div>

        {/* City List */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">
              Cities ({children.length})
            </label>
            <button
              type="button"
              onClick={handleRediscoverCities}
              disabled={isRediscovering || isSaving}
              className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 disabled:opacity-50"
            >
              {isRediscovering ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5" />
              )}
              Re-discover cities
            </button>
          </div>

          {loadingChildren ? (
            <div className="flex items-center justify-center p-6">
              <Loader2 className="w-6 h-6 text-primary-600 animate-spin" />
            </div>
          ) : (
            <div className="space-y-1 max-h-[240px] overflow-y-auto">
              {children.map((city) => (
                <div
                  key={city.id}
                  className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-200 bg-white"
                >
                  <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-900">{city.city}</span>
                    {city.country !== region.country && (
                      <span className="text-xs text-gray-500 ml-2">{city.country}</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleExtract(city.id)}
                    disabled={extractingId === city.id || isSaving}
                    className="text-gray-400 hover:text-amber-600 flex-shrink-0 disabled:opacity-50"
                    title="Extract to standalone"
                  >
                    {extractingId === city.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <ArrowLeftFromLine className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemoveCity(city.id)}
                    disabled={removingCityId === city.id || isSaving}
                    className="text-gray-400 hover:text-red-500 flex-shrink-0 disabled:opacity-50"
                    title="Remove city"
                  >
                    {removingCityId === city.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <X className="w-4 h-4" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add City */}
          {!showAddCity ? (
            <button
              type="button"
              onClick={() => setShowAddCity(true)}
              disabled={isSaving}
              className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-800 transition-colors disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              Add city
            </button>
          ) : (
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Add City</span>
                <button
                  type="button"
                  onClick={() => { setShowAddCity(false); setSearchValue(''); setManualCityName(''); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Search for city */}
              <LocationAutocomplete
                value={searchValue}
                onChange={setSearchValue}
                onLocationSelect={handleAddCityFromSearch}
                placeholder="Search for a city..."
                types="cities"
                disabled={addingCity}
              />

              {/* Manual entry */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">or type manually:</span>
              </div>
              <div className="flex gap-2">
                <Input
                  value={manualCityName}
                  onChange={(e) => setManualCityName(e.target.value)}
                  placeholder="City name"
                  className="flex-1"
                  disabled={addingCity}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddManualCity();
                    }
                  }}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleAddManualCity}
                  disabled={!manualCityName.trim() || addingCity}
                  isLoading={addingCity}
                >
                  Add
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Move Existing Destinations */}
        {standaloneDestinations.length > 0 && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Move Existing Destinations
            </label>
            <p className="text-xs text-gray-500">
              Move standalone city destinations into this region. All shortlist data is preserved.
            </p>
            <div className="space-y-1 max-h-[160px] overflow-y-auto">
              {standaloneDestinations
                .filter(d => !children.some(c => c.id === d.id))
                .map((dest) => (
                  <div
                    key={dest.id}
                    className="flex items-center gap-3 p-2.5 rounded-lg border border-dashed border-gray-300 bg-gray-50"
                  >
                    <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-gray-700">{dest.city}</span>
                      <span className="text-xs text-gray-400 ml-2">{dest.country}</span>
                      {(dest.shortlist_count > 0) && (
                        <Badge variant="secondary" className="ml-2 text-xs">
                          {dest.shortlist_count} shortlisted
                        </Badge>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleMoveIn(dest.id)}
                      disabled={movingInId === dest.id || isSaving}
                      isLoading={movingInId === dest.id}
                      className="flex-shrink-0 gap-1 text-xs"
                    >
                      <ArrowRightToLine className="w-3.5 h-3.5" />
                      Move In
                    </Button>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Warning if no cities */}
        {!loadingChildren && children.length === 0 && (
          <Alert variant="warning">
            <AlertCircle className="w-4 h-4" />
            <span>A region must have at least one city.</span>
          </Alert>
        )}

        {/* Error */}
        {error && (
          <Alert variant="error">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </Alert>
        )}
      </div>
    </Modal>
  );
}
